import { isFloatingPanelOpen } from '../floatingPanel.tsx';
import { isTypingInField } from '../overlayTyping.ts';
import { overlayStore } from '../panels.ts';

/**
 * What Escape should do before a scene opens the pause menu.
 * `defer` — some other listener already closes this chrome.
 * `dialog` — a confirm/save dialog has no key listener of its own; cancel it here.
 */
export type EscapeChrome = 'defer' | 'dialog';

export type EscapeChromeProbe = {
  typing: boolean;
  floating: boolean;
  colorPicker: boolean;
  openMap: boolean;
  dialog: boolean;
  saveDialog: boolean;
  logModal: boolean;
  scrubSlider: boolean;
  menuOpen: boolean;
  flyoutOpen: boolean;
};

export function classifyEscapeChrome(probe: EscapeChromeProbe): EscapeChrome | null {
  if (
    probe.floating ||
    probe.colorPicker ||
    probe.logModal ||
    probe.scrubSlider ||
    probe.menuOpen ||
    probe.flyoutOpen
  ) {
    return 'defer';
  }

  // A dialog field is focused, but Escape still cancels the dialog rather than
  // falling through to leave a test play.
  if (probe.dialog || probe.saveDialog) {
    return 'dialog';
  }

  if (probe.typing) {
    return 'defer';
  }

  if (probe.openMap) {
    return 'defer';
  }

  return null;
}

function expanded(selector: string): boolean {
  return document.querySelector(selector) !== null;
}

export function readEscapeChrome(): EscapeChrome | null {
  const panels = overlayStore.get();

  return classifyEscapeChrome({
    typing: isTypingInField(),
    floating: isFloatingPanelOpen(),
    colorPicker: panels.colorPicker !== null,
    openMap: panels.editorOpenMap !== null,
    dialog: panels.editorDialog !== null,
    saveDialog: panels.editorSaveDialog !== null,
    logModal: expanded('.editor-log-modal'),
    scrubSlider: expanded('.ui-scrub-slider-popover'),
    menuOpen: expanded('.editor-menu-trigger[aria-expanded="true"]'),
    flyoutOpen: expanded('.editor-rail-button[aria-expanded="true"]'),
  });
}

/** Cancel a dialog that does not listen for Escape itself. Returns true when one was open. */
export function cancelBareDialog(): boolean {
  const panels = overlayStore.get();

  if (panels.editorSaveDialog) {
    panels.editorSaveDialog.onCancel();
    return true;
  }

  if (panels.editorDialog) {
    panels.editorDialog.onCancel();
    return true;
  }

  return false;
}
